import { JobseekerService } from '@admin/jobseekers/shared/services/jobseeker.service';
import { Validator } from './../../shared/validation/validators';
import { Utils } from '../../shared/utils';
import { OnChanges } from '@angular/core';
import { FieldState } from '../../shared/models/field-state';
import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { JobseekerModel } from '@admin/jobseekers/shared/models/jobseeker-model';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { IValidator } from '@admin/shared/validation/validators';
import { Patterns } from '@admin/shared/validation/patterns';
import { ConfigurationService } from '../../shared/services/configuration.service';
import { UserService } from '@admin/core/user.service';
import { FeatureEnum } from '@admin/shared/models/feature-enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-jobseeker-basic',
  templateUrl: './jobseeker-basic.component.html',
  styleUrls: ['./jobseeker-basic.component.less'],
  standalone: false,
})
export class JobseekerBasicComponent implements OnInit, OnChanges {
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobseekerInput'].currentValue != null) {
      this.jobseeker = Utils.cloneObject(this.jobseekerInput, JobseekerModel);
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      fullName: new FieldState(
        null,
        () =>
          this.jobseeker &&
          (this.jobseeker.name != null || this.jobseeker.surname != null)
      ),
      name: new FieldState(() => this.jobseeker && this.jobseeker.name),
      surname: new FieldState(() => this.jobseeker && this.jobseeker.surname),
      phone: new FieldState(
        () =>
          this.jobseeker &&
          (this.jobseeker.phone != null || this.jobseeker.phone2 != null)
      ),
      phone1: new FieldState(() => this.jobseeker && this.jobseeker.phone),
      phone2: new FieldState(() => this.jobseeker && this.jobseeker.phone2),
      email: new FieldState(() => this.jobseeker && this.jobseeker.email),
      username: new FieldState(() => this.jobseeker && this.jobseeker.username),
      languageId: new FieldState(
        () => this.jobseeker && this.jobseeker.languageId
      ),
    };
  }

  constructor(
    private translationService: TranslateService,
    private config: ConfigurationService,
    private jobseekerService: JobseekerService,
    private userService: UserService
  ) {
    this.jobseeker = new JobseekerModel();
  }

  @Output()
  public onUpdate: EventEmitter<JobseekerModel> =
    new EventEmitter<JobseekerModel>();

  @Input('jobseeker')
  public jobseekerInput: JobseekerModel;

  public jobseeker: JobseekerModel;

  @Input()
  public editingAllowed: boolean;

  public fullNameOriginalState: { name: string; surname: string };

  public phoneOriginalState: { phone: string; phone2: string };

  public emailOriginalState: { email: string };

  public usernameOriginalState: { username: string };

  public preferredLanguageOriginalState: { languageId: string };

  public resetPassword: string;

  public isInEditMode: boolean;

  public dateFormat: string;

  public primaryLanguage: string;
  public alternativeLanguage: string;

  public hasInternalEmployeeManagePermission: boolean;

  public fieldStates: {
    fullName: FieldState;
    name: FieldState;
    surname: FieldState;
    phone: FieldState;
    phone1: FieldState;
    phone2: FieldState;
    email: FieldState;
    username: FieldState;
    languageId: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  public onFullNameEditClik() {
    this.saveFullNameOriginalState();
    this.fieldStates.fullName.isInEditMode = true;
    this.isInEditMode = true;
  }

  public onFullNameEditCancelClick() {
    this.fieldStates.fullName.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreFullNameOriginalState();
    this.initialize();
  }

  public onFullNameSaveClick() {
    let jobseeker = this.jobseeker;
    var validators = this.getFullNameValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService.saveJobseekerBasicInfo(jobseeker).subscribe(() => {
        this.initialize();
        this.onUpdate.emit(jobseeker);
      });
    });
  }

  public onEmailSaveClick() {
    let jobseeker = this.jobseeker;
    var validators = this.getEmailValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService.saveJobseekerBasicInfo(jobseeker).subscribe(() => {
        this.initialize();
        this.onUpdate.emit(jobseeker);
      });
    });
  }

  public onUsernameSaveClick() {
    let jobseeker = this.jobseeker;
    var validators = this.getUsernameValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService.saveJobseekerBasicInfo(jobseeker).subscribe(() => {
        this.initialize();
        this.onUpdate.emit(jobseeker);
      });
    });
  }

  onEditJobseekerPreferredLanguage() {
    this.jobseekerService
      .updateJobseekerPreferredLanguage(
        this.jobseeker.jobseekerId,
        this.jobseeker.languageId
      )
      .subscribe(() => {
        this.initialize();
      });
  }

  public onPhoneSaveClick() {
    let jobseeker = this.jobseeker;
    var validators = this.getPhoneValidators();
    this.invokeValidation(validators, () => {
      this.jobseekerService.saveJobseekerBasicInfo(jobseeker).subscribe(() => {
        this.initialize();
        this.onUpdate.emit(jobseeker);
      });
    });
  }

  private initialize() {
    this.initializeFieldsState();
    this.isInEditMode = false;
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFullNameValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );

    return [
      Validator.forRequired(field.name, () => jobseeker.name, emptyMessage),
      Validator.forPattern(
        field.name,
        () => jobseeker.name,
        Patterns.personName,
        incorrectFormat
      ),
      Validator.forRequired(
        field.surname,
        () => jobseeker.surname,
        emptyMessage
      ),
      Validator.forPattern(
        field.surname,
        () => jobseeker.surname,
        Patterns.personName,
        incorrectFormat
      ),
    ];
  }

  private getEmailValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );

    return [
      Validator.forRequired(field.email, () => jobseeker.email, emptyMessage),
      Validator.forPattern(
        field.email,
        () => jobseeker.email,
        Patterns.emailNew,
        incorrectFormat
      ),
    ];
  }

  private getUsernameValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );

    return [
      Validator.forRequired(
        field.username,
        () => jobseeker.username,
        emptyMessage
      ),
      Validator.forPattern(
        field.username,
        () => jobseeker.username,
        Patterns.emailNew,
        incorrectFormat
      ),
    ];
  }

  private getPhoneValidators() {
    let field = this.field;
    let jobseeker = this.jobseeker;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let incorrectPhone = this.translationService.instant(
      'message.PhoneNumberIncorrectFormat'
    );

    return jobseeker.phone2 != null && jobseeker.phone2 != ''
      ? [
          Validator.forRequired(
            field.phone2,
            () => jobseeker.phone2,
            emptyMessage
          ),
          Validator.forPattern(
            field.phone2,
            () => jobseeker.phone2,
            Patterns.phoneNumber,
            incorrectPhone
          ),
          Validator.forNotRequired(field.phone1, () => jobseeker.phone),
          Validator.forPattern(
            field.phone1,
            () => jobseeker.phone,
            Patterns.phoneNumber,
            incorrectPhone
          ),
        ]
      : [
          Validator.forRequired(
            field.phone1,
            () => jobseeker.phone,
            emptyMessage
          ),
          Validator.forPattern(
            field.phone1,
            () => jobseeker.phone,
            Patterns.phoneNumber,
            incorrectPhone
          ),
          Validator.forNotRequired(field.phone2, () => jobseeker.phone2),
          Validator.forPattern(
            field.phone2,
            () => jobseeker.phone2,
            Patterns.phoneNumber,
            incorrectPhone
          ),
        ];
  }

  private saveFullNameOriginalState() {
    this.fullNameOriginalState = {
      name: this.jobseeker.name,
      surname: this.jobseeker.surname,
    };
  }

  private restoreFullNameOriginalState() {
    this.jobseeker.name = this.fullNameOriginalState.name;
    this.jobseeker.surname = this.fullNameOriginalState.surname;
  }

  public onPhoneEditClick() {
    this.savePhoneOriginalState();
    this.fieldStates.phone.isInEditMode = true;
    this.isInEditMode = true;
  }

  public onPhoneEditCancelClick() {
    this.fieldStates.phone.isInEditMode = false;
    this.isInEditMode = false;
    this.restorePhoneOriginalState();
    this.initialize();
  }

  private savePhoneOriginalState() {
    this.phoneOriginalState = {
      phone: this.jobseeker.phone,
      phone2: this.jobseeker.phone2,
    };
  }

  private restorePhoneOriginalState() {
    this.jobseeker.phone = this.phoneOriginalState.phone;
    this.jobseeker.phone2 = this.phoneOriginalState.phone2;
  }

  public onEmailEditClick() {
    this.saveEmailOriginalState();
    this.fieldStates.email.isInEditMode = true;
    this.isInEditMode = true;
  }

  public onUsernameEditClick() {
    this.saveUsernameOriginalState();
    this.fieldStates.username.isInEditMode = true;
    this.isInEditMode = true;
  }

  public onLanguageIdEditClick() {
    //this.saveUsernameOriginalState();
    this.savePreferredLanguageOriginalState();
    this.fieldStates.languageId.isInEditMode = true;
    this.isInEditMode = true;
  }

  public onEmailEditCancelClick() {
    this.fieldStates.email.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreEmailOriginalState();
    this.initialize();
  }

  public onUsernameEditCancelClick() {
    this.fieldStates.username.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreUsernameOriginalState();
    this.initialize();
  }

  public onJobseekerPreferredLanguageEditCancelClick() {
    this.fieldStates.languageId.isInEditMode = false;
    this.isInEditMode = false;
    this.restoreJobseekerPreferredLanguageOriginalState();
    this.initialize();
  }

  private saveEmailOriginalState() {
    this.emailOriginalState = { email: this.jobseeker.email };
  }

  private saveUsernameOriginalState() {
    this.usernameOriginalState = { username: this.jobseeker.username };
  }

  private savePreferredLanguageOriginalState() {
    this.preferredLanguageOriginalState = {
      languageId: this.jobseeker.languageId,
    };
  }

  private restoreEmailOriginalState() {
    this.jobseeker.email = this.emailOriginalState.email;
  }

  private restoreUsernameOriginalState() {
    this.jobseeker.username = this.usernameOriginalState.username;
  }

  private restoreJobseekerPreferredLanguageOriginalState() {
    this.jobseeker.languageId = this.preferredLanguageOriginalState.languageId;
  }

  public onResetPasswordClick(panel, event) {
    let jobseekerId = this.jobseeker && this.jobseeker.jobseekerId;
    this.jobseekerService
      .getTemporaryPassword(jobseekerId)
      .subscribe((result) => {
        this.resetPassword = result.resetPassword;
        panel.toggle(event);
      });
  }

  public clearPassword(event: any) {
    this.resetPassword = null;
  }

  public onFlagUnflagInternalEmployee() {
    this.jobseeker.isInternalEmployee = !this.jobseeker.isInternalEmployee;
    let jobseeker = this.jobseeker;
    this.jobseekerService.saveInternalEmployeeState(jobseeker).subscribe(() => {
      this.initialize();
      this.onUpdate.emit(jobseeker);
    });
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.dateFormat = config.dateFormat;
    this.primaryLanguage = config.primaryLanguage;
    this.alternativeLanguage = config.alternativeLanguage;

    this.hasInternalEmployeeManagePermission =
      this.userService.currentUser.features.some(
        (f) => f == FeatureEnum.FlagAndUnflagInternalEmployeeJobseeker
      );
  }
}
